import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Upload, File, Lock } from 'lucide-react';

const DocumentDialog = ({
  open,
  onOpenChange,
  selectedDocument,
  documentForm,
  setDocumentForm,
  selectedFile,
  setSelectedFile,
  uploadProgress,
  isUploading,
  formLoading,
  onSave
}) => {
  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      setSelectedFile(null);
    }
    onOpenChange(newOpen);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill name if empty
      if (!documentForm.name) {
        setDocumentForm({ ...documentForm, name: file.name.replace(/\.[^/.]+$/, '') });
      }
    }
  };

  const formatFileSize = (size) => {
    if (size > 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(size / 1024).toFixed(1)} KB`;
  };

  const isFileTooLarge = selectedFile && selectedFile.size > 10 * 1024 * 1024;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            {selectedDocument ? 'Edit Document' : 'Add Document'}
          </DialogTitle>
          <DialogDescription>Upload and categorize documents for this LLC</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Document Type</Label>
            <Select 
              value={documentForm.document_type} 
              onValueChange={(v) => setDocumentForm({ ...documentForm, document_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_statement">Bank Statement</SelectItem>
                <SelectItem value="tax_return">Tax Return</SelectItem>
                <SelectItem value="other">Other Document</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Document Name</Label>
            <Input
              value={documentForm.name}
              onChange={(e) => setDocumentForm({ ...documentForm, name: e.target.value })}
              placeholder="e.g., 2024 Q4 Bank Statement"
            />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input
              value={documentForm.description}
              onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
              placeholder="Additional notes about this document"
            />
          </div>

          {/* Password Protected Checkbox */}
          <div className="flex items-center space-x-2 py-2">
            <Checkbox
              id="password-protected"
              checked={documentForm.is_password_protected || false}
              onCheckedChange={(checked) => setDocumentForm({ ...documentForm, is_password_protected: checked })}
            />
            <label
              htmlFor="password-protected"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer"
            >
              <Lock className="w-4 h-4 text-amber-600" />
              Password-protected document
            </label>
          </div>

          {/* File Upload Section */}
          <div className="space-y-2">
            <Label>File Upload</Label>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-slate-400 transition-colors">
              <input
                type="file"
                id="document-file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                onChange={handleFileChange}
              />
              <label
                htmlFor="document-file"
                className="flex flex-col items-center gap-2 cursor-pointer"
              >
                {selectedFile ? (
                  <>
                    <File className={`w-8 h-8 ${isFileTooLarge ? 'text-rose-600' : 'text-emerald-600'}`} />
                    <span className="text-sm font-medium text-slate-700">{selectedFile.name}</span>
                    <span className={`text-xs ${isFileTooLarge ? 'text-rose-600 font-medium' : 'text-slate-500'}`}>
                      {formatFileSize(selectedFile.size)}
                      {isFileTooLarge && ' (exceeds 10MB limit!)'}
                    </span>
                  </>
                ) : selectedDocument?.cloudinary_url ? (
                  <>
                    <File className="w-8 h-8 text-blue-600" />
                    <span className="text-sm text-slate-600">File already uploaded</span>
                    <span className="text-xs text-slate-400">Select a new file to replace</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-400" />
                    <span className="text-sm text-slate-600">Click to select a file</span>
                    <span className="text-xs text-slate-400">PDF, Images, Word, Excel (max 10MB)</span>
                  </>
                )}
              </label>
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={formLoading || isUploading || !documentForm.name || isFileTooLarge}
          >
            {(formLoading || isUploading) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isUploading ? 'Uploading...' : 'Save Document'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentDialog;
